import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { showLoading, hideLoading } from "@/config/serviceLoading";
import { Message } from "@arco-design/web-react";
import { checkStatus } from "@/config/httpStatus";
import { getTokenToLocalStorage } from "./token";

// 定义接口类型
declare module "axios" {
  interface AxiosRequestConfig {
    /**@name loading 是否展示加载 */
    loading?: boolean;
  }
}

// 创建axios实例
const service = axios.create({
  baseURL: "/api/v1",
  timeout: 10000,
  withCredentials: false, // 跨域请求时是否需要使用凭证
});

// 隐藏所有loading
const hideAllLoading = (message?: string) => {
  setTimeout(() => {
    hideLoading(() => {
      if (message) Message.error(message);
      // NProgress.done();
    });
  }, 200);
};

// 请求拦截器
service.interceptors.request.use((config) => {
  // 网页加载效果
  showLoading(config?.loading, () => {
    // NProgress.start();
  });
  // 请求头配置
  const _headers: any = { ...config.headers };
  const token = getTokenToLocalStorage();
  if (token) {
    _headers["Token"] = token;
  }
  _headers["Device"] = "web_client";
  config.headers = _headers;
  return config;
});

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, status, config } = response;
    // 失败返回
    if (data.code != 0 && config?.url !== "/graphql/") {
      // 无权限
      if (data.code === 401 || status === 401) {
        window.localStorage.clear();
        const pathName = window.location.pathname;

        if (pathName !== "/work-bench") {
          window.location.href = "/work-bench";
        }
        hideAllLoading("登录过期，请重新登录");
        return Promise.reject(data);
      }
      hideAllLoading(data?.message);
      return Promise.reject(data);
    }
    hideAllLoading();
    return response;
  },
  (error) => {
    const { response } = error;
    if (response && response.status === 401) {
      window.localStorage.clear();
      const pathName = window.location.pathname;
      if (pathName !== "/work-bench") {
        window.location.href = "/work-bench";
      }
      hideAllLoading();
      return Promise.reject(error);
    }
    if (response && response.status === 400) {
      hideAllLoading();
      return Promise.reject(response.data.message);
    }
    // 根据响应的错误状态码，做不同的处理
    if (response) checkStatus(response.status);
    // 断网处理
    if (!window.navigator.onLine) window.location.href = "/500";
    hideAllLoading();
    return Promise.reject(error);
  },
);

/**
 * 请求方法封装
 * @description 定义请求参数/返回参数类型
 * @param config 请求配置参数
 * @returns
 */
const request = <T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return service(config);
};

const get = <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return request({ url, method: "get", params, ...config });
};

const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return request({ url, method: "post", data, ...config });
};

const put = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return request({ url, method: "put", data, ...config });
};

const deleteRequest = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return request({ url, method: "delete", data, ...config });
};

const graphql = (data: string) =>
  post(`/graphql/`, data, {
    headers: {
      "Content-type": "application/graphql",
    },
  });

export default { request, get, post, put, delete: deleteRequest, graphql };
